import React, { memo } from 'react';
export const ModulesSection = () => {
  const modules = [{
    num: '1',
    icon: '🧠',
    title: 'Sharp Mind Workbook',
    subtitle: 'Cognitive Stimulation · 150+ Activities · Large Print',
    desc: '30 pages of high-quality cognitive activities for older adults. Memory, attention, language and calculation organized in large-print worksheets. Family members can guide and support without prior medical training.',
    includes: ['150+ cognitive stimulation activities', 'Memory, attention and language exercises', 'Clear instructions for caregivers', 'Response space on each activity', 'Monthly cognitive progress tracker', 'Evidence-based content updated 2026']
  }, {
    num: '2',
    icon: '🦵',
    title: 'Move with Confidence',
    subtitle: 'At-Home Senior Mobility · Seated and Standing Exercises · Fall Prevention',
    desc: '30 pages of adapted physical exercises for older adults. Seated routines, balance exercises and fall prevention protocol. Clear illustrations for each exercise. No special equipment required.',
    includes: ['Complete seated routines for limited mobility', 'Supported balance and strength exercises', 'Step-by-step fall prevention protocol', 'Clear illustrations of each position', 'Weekly progression chart', 'Warning signs to stop exercise safely']
  }, {
    num: '3',
    icon: '🏥',
    title: 'Expert Care Protocol',
    subtitle: "Alzheimer's · Parkinson's · Diabetes · High Blood Pressure",
    desc: '30 pages of updated protocols for supporting the most common conditions in older adults. What to do, what to avoid and when to call the doctor. Written for families and caregivers without medical training.',
    includes: ["Specific protocol for Alzheimer's and dementia", "Daily management guide for Parkinson's", 'At-home monitoring for Diabetes and High Blood Pressure', 'Warning signs requiring immediate action', 'Senior-specific first aid guidance', 'Emergency resources and contacts']
  }, {
    num: '4',
    icon: '📋',
    title: 'Vitality 360 Health Tracker',
    subtitle: 'Medication · Blood Pressure · Blood Sugar · Mood · Sleep',
    desc: '30 pages of health tracking forms designed to share with healthcare providers at every appointment. Daily medication tracking, blood pressure, blood sugar, mood and sleep. The organized records healthcare professionals appreciate.',
    includes: ['Complete personal health profile', 'Weekly medication log with refill tracking', 'Blood pressure and blood sugar charts', 'Daily mood and emotional wellbeing log', 'Sleep quality tracking', 'Medical appointment and test results log']
  }, {
    num: '5',
    icon: '⭐',
    title: 'The Empowered Caregiver Guide',
    subtitle: 'Organization · Boundaries · Self-Care · Burnout Prevention · Family Support',
    desc: '30 pages designed for caregivers. How to organize your day without exhaustion, how to set healthy boundaries with family and how to prevent caregiver burnout. So the person providing care can also live well.',
    includes: ['Time-block system for daily caregiving', 'Validated caregiver burnout assessment', '5-minute stress relief techniques', 'Family care map to distribute responsibilities', 'Weekly caregiver self-care plan', 'Support resources and networks']
  }];
  const inclusions = ['30 pages of focused content', 'Clear illustrations for each activity', 'Instructions for caregivers and family members', 'Large, readable print throughout', 'Printable trackers and logs', 'Evidence-based recommendations updated 2026', 'Validated scientific references', 'Professional branding and 2026 date on every page'];
  return <section id="modulos" className="bg-familia-card py-10 md:py-16 px-4 md:px-6" data-id="element-142">
      <div className="max-w-7xl mx-auto w-[92%] md:w-full" data-id="element-143">
        <div className="text-center mb-10 md:mb-16" data-id="element-144">
          <h2 className="text-[22px] sm:text-3xl md:text-5xl font-display font-bold mb-4 md:mb-6" data-id="element-145">
            📚 THE 5 CORE MODULES — <br className="hidden sm:block" data-id="element-146" /> For
            comprehensive senior care
          </h2>
          <p className="text-familia-gray text-[15px] md:text-lg max-w-3xl mx-auto leading-relaxed" data-id="element-147">
            Each module is a complete system with activities for older adults,
            guidance for caregivers or family members, health trackers updated
            for 2026, clear instructions and evidence-based recommendations on
            every page.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6 mb-10 md:mb-16" data-id="element-148">
          {modules.map((m, i) => <div key={i} className="bg-familia-section border border-familia-cyan/25 p-6 md:p-8 rounded-2xl card-hover flex flex-col" data-id="element-149">
              <div className="flex items-center justify-between mb-4" data-id="element-150">
                <span className="text-familia-cyan font-mono font-bold text-xs sm:text-sm" data-id="element-151">
                  MODULE {m.num}
                </span>
                <span className="text-3xl md:text-4xl" data-id="element-152">{m.icon}</span>
              </div>
              <h3 className="text-lg md:text-xl font-display font-bold mb-1" data-id="element-153">
                {m.title}
              </h3>
              <p className="text-familia-gold text-xs sm:text-sm font-semibold mb-4" data-id="element-154">
                {m.subtitle}
              </p>
              <p className="text-familia-gray text-[14px] md:text-sm leading-relaxed mb-6" data-id="element-155">
                {m.desc}
              </p>
              <div className="border-t border-white/10 pt-4 mt-auto" data-id="element-156">
                <p className="text-xs font-bold text-white/90 uppercase tracking-wider mb-3" data-id="element-157">
                  Includes:
                </p>
                <ul className="space-y-2" data-id="element-158">
                  {m.includes.map((item, j) => <li key={j} className="flex items-start gap-2 text-[13px] text-familia-gray" data-id="element-159">
                      <span className="text-familia-cyan shrink-0" data-id="element-160">→</span>
                      <span data-id="element-161">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>)}
        </div>
        <div className="bg-familia-bg border border-familia-cyan/30 border-dashed p-6 md:p-12 rounded-3xl max-w-4xl mx-auto" data-id="element-162">
          <h4 className="text-lg md:text-xl font-display font-bold mb-6 md:mb-8 text-center" data-id="element-163">
            Every Module Includes:
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 md:gap-x-12 gap-y-3 md:gap-y-4" data-id="element-164">
            {inclusions.map((item, i) => <div key={i} className="flex items-center gap-3 md:gap-4" data-id="element-165">
                <span className="text-familia-green text-lg md:text-xl" data-id="element-166">
                  ✅
                </span>
                <span className="text-[14px] sm:text-base font-medium" data-id="element-167">
                  {item}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};